import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { confirm } from "../../common/Confirm/confirm";
import { useAuth } from "../../../../context/AuthContext.js";
import { toast } from '../../common/Toast/toast';
import { Search, X, Filter, UserPlus, MessageSquarePlus } from "lucide-react";
import moment from "moment";
import debounce from "lodash/debounce";

// Components
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import AddBatch from "./AddBatch.jsx";
import BatchStudentsManager from "./BatchStudentsManager.jsx";
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import "../../Trainer/TrainerDashboard.css";
import "../../common/lms-pages.css";
// .wp-link, for the Details tab's "See all notes".
import "../../common/waypoint/waypoint.css";
import NewCustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";

// Services
import {
  getBatches,
  deleteBatch,
  createChatGroup,
  deleteChatGroup,
  syncChatMembers,
  getBatchNotes,
  getRecentBatchNotes,
} from "../../../../services/BatchService.js";
import { getcourses } from "../../../../services/CourseModule.js";
import { getTrainers, getTrainerByUserId } from "../../../../services/TrainerService.js";
import { getClassrooms } from "../../../../services/ClassroomService.js";
import { getMe } from "../../../../services/AuthService.js";
import { initialSearchFromUrl } from '../../../../utils/urlSearch';
import FilterSheet from '../../common/FilterSheet/FilterSheet';
import useDebouncedCallback from '../../common/useDebouncedCallback';

const ListBatch = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  // Seeded from ?search= so a hit in the topbar search opens this list
  // already filtered to the row that was clicked.
  const [searchTerm, setSearchTerm] = useState(initialSearchFromUrl);
  const [searchField, setSearchField] = useState("name"); // Default to "name"
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Store trainer ID from trainer collection (actual ObjectId)
  const [actualTrainerId, setActualTrainerId] = useState('');
  const [trainerDataLoaded, setTrainerDataLoaded] = useState(false);

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] = useState(null);
  const [selectedTrainerFilter, setSelectedTrainerFilter] = useState(null);
  const [appliedCourseFilter, setAppliedCourseFilter] = useState("");
  const [appliedTrainerFilter, setAppliedTrainerFilter] = useState("");
  const [selectedClassroomFilter, setSelectedClassroomFilter] = useState(null);
  const [appliedClassroomFilter, setAppliedClassroomFilter] = useState("");

  // Classroom dropdown: searched and paged by the API, like the other two.
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [classroomSearchTerm, setClassroomSearchTerm] = useState("");
  const [classroomPage, setClassroomPage] = useState(1);
  const [hasMoreClassrooms, setHasMoreClassrooms] = useState(true);
  const [classroomHasLoadedInitial, setClassroomHasLoadedInitial] = useState(false);
  const isFetchingClassrooms = useRef(false);

  // Dropdown options
  const [courseOptions, setCourseOptions] = useState([]);
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [trainerSearchTerm, setTrainerSearchTerm] = useState("");
  const [coursePage, setCoursePage] = useState(1);
  const [trainerPage, setTrainerPage] = useState(1);
  const [hasMoreCourses, setHasMoreCourses] = useState(true);
  const [hasMoreTrainers, setHasMoreTrainers] = useState(true);
  const isFetchingCourses = useRef(false);
  const isFetchingTrainers = useRef(false);
  
  // Track initial load state for dropdowns
  const [trainerHasLoadedInitial, setTrainerHasLoadedInitial] = useState(false);
  const [courseHasLoadedInitial, setCourseHasLoadedInitial] = useState(false);

  // Modal states
  const [showBatchDetailsModal, setShowBatchDetailsModal] = useState(false);
  const [batchTab, setBatchTab] = useState("details");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  /* The Batch Notes tab. Loaded when the tab is opened, not with the list:
     the list rows carry only `noteCount` (for the badge), so a page of
     batches does not ship every note on every row. */
  const [batchNotes, setBatchNotes] = useState([]);
  const [notesState, setNotesState] = useState("idle"); // idle | loading | ready | failed
  const selectedBatchId = selectedBatch?.id;
  useEffect(() => {
    if (batchTab !== "notes" || !selectedBatchId) return undefined;
    let cancelled = false;
    setNotesState("loading");
    getBatchNotes(selectedBatchId)
      .then((rows) => {
        if (cancelled) return;
        setBatchNotes(rows);
        setNotesState("ready");
      })
      .catch(() => {
        if (!cancelled) setNotesState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [batchTab, selectedBatchId]);

  /* The Details tab's "Recent notes": the latest five, cut by the API, and
     the batch's whole count for "see all". Loaded with the Details tab, so a
     popup opened for anything else does not ask. */
  const RECENT_NOTES = 5;
  const [recentNotes, setRecentNotes] = useState({ items: [], total: 0, state: "idle" });
  useEffect(() => {
    if (batchTab !== "details" || !selectedBatchId) return undefined;
    let cancelled = false;
    setRecentNotes((prev) => ({ ...prev, state: "loading" }));
    getRecentBatchNotes(selectedBatchId, RECENT_NOTES)
      .then((r) => {
        if (!cancelled) setRecentNotes({ ...r, state: "ready" });
      })
      .catch(() => {
        if (!cancelled) setRecentNotes({ items: [], total: 0, state: "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [batchTab, selectedBatchId]);

  // Editing a batch used to navigate to /add-batch/:id and leave the list
  // behind. It opens in a dialog now, the same way the events list edits an
  // event. `{ recordId }` so the same dialog can carry a create later.
  const [formModal, setFormModal] = useState(null);
  // The row's "Students" popup: add and remove students without opening
  // the whole edit form. Holds { id, name } of the batch, or null.
  const [manageBatch, setManageBatch] = useState(null);
  // A change saved in any of the three popups is confirmed in that popup
  // (the done panel), which then closes itself. Opened over the view, the
  // form is the newer popup, so its save lands in the form.
  const { done: batchDone } = usePopupDone(Boolean(showBatchDetailsModal));
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const { done: manageDone } = usePopupDone(Boolean(manageBatch));

  const navigate = useNavigate();
  const hasLoaded = useRef(false);
  const hasShownToast = useRef(false);
  const didFetchProfile = useRef(false);
  const { hasPermission } = useAuth();

  // Permission check
  useEffect(() => {
    if (!hasPermission("batches", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view Batches.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Helper functions
  const first = (v) => (Array.isArray(v) ? v[0] : v);

  const getCourseName = (courseField) => {
    const c = first(courseField);
    if (c && typeof c === "object") return c.name || "N/A";
    return c || "N/A";
  };

  const getTrainerName = (trainerField) => {
    const t = first(trainerField);
    if (t && typeof t === "object") return t.trainerName || "N/A";
    return t || "N/A";
  };

  const getClassroomName = (classroomField) => {
    const cl = first(classroomField);
    if (cl && typeof cl === "object") return cl.classroomNo || "N/A";
    return cl || "N/A";
  };

  // The batch's own branch, or its classroom's when it has none (the API decides).
  const getBranchName = (branchField) => {
    const b = first(branchField);
    if (b && typeof b === "object") return String(b.name || "").trim() || "N/A";
    return "N/A";
  };

  // Get student count from userId array (objects or strings)
  const getStudentCount = (userId) => {
    if (!userId) return { count: 0, text: "No students" };
    if (Array.isArray(userId)) {
      const count = userId.length;
      return {
        count,
        text: `${count} Student${count !== 1 ? "s" : ""}`,
      };
    }
    // Fallback for string (comma-separated) - should not happen with new API
    if (typeof userId === "string") {
      const ids = userId.split(",").filter(Boolean);
      const count = ids.length;
      return {
        count,
        text: `${count} Student${count !== 1 ? "s" : ""}`,
      };
    }
    return { count: 0, text: "No students" };
  };

  // Extract students array from batch.userId (array of objects)
  const getStudentsFromBatch = (batch) => {
    if (!batch.userId) return [];
    if (Array.isArray(batch.userId)) {
      // Map to a consistent structure
      return batch.userId.map((student) => ({
        studentId: student.id || student._id,
        studentName: student.name || student.studentName || "Unknown",
        studentEmail: student.email || student.studentEmail || "N/A",
        phone: student.phone || student.studentNumber || "N/A",
      }));
    }
    return [];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("MMMM Do YYYY, h:mm A");
  };

  // Fetch user profile and get actual trainer ID
  const fetchUserProfile = useCallback(async () => {
    if (didFetchProfile.current) return;
    didFetchProfile.current = true;

    try {
      const response = await getMe();
      const userData = response.data || response;
      
      if (userData.isSuperAdmin === true) {
        setIsAdmin(true);
        setIsTrainer(false);
        setTrainerDataLoaded(true);
      } else {
        const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
        const isTrainerUser = profileLabel === 'trainer';
        setIsTrainer(isTrainerUser);
        setIsAdmin(false);
        
        if (isTrainerUser) {
          const userId = userData.id || userData._id || userData.userId;
          
          try {
            const trainerResponse = await getTrainerByUserId(userId);
            if (trainerResponse && trainerResponse.data) {
              const trainerObjectId = trainerResponse.data.id || trainerResponse.data._id;
              setActualTrainerId(trainerObjectId);
              console.log("Actual Trainer ID set to:", trainerObjectId);
            } else {
              setActualTrainerId(userId);
            }
          } catch (error) {
            console.error("Error fetching trainer data:", error);
            setActualTrainerId(userId);
          }
        }
      }
      setCurrentUser(userData);
      setTrainerDataLoaded(true);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setTrainerDataLoaded(true);
    }
  }, []);

  // Fetch courses for dropdown
  const fetchCourses = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingCourses.current = true;
      setLoadingCourses(true);
      const response = await getcourses(20, pageNum, search, {
        includeTotal: false,
      });
      const courses = response?.data || [];
      const hasMore = courses.length === 20;
      return { courses, hasMore };
    } catch (err) {
      console.error("Error fetching courses:", err);
      return { courses: [], hasMore: false };
    } finally {
      setLoadingCourses(false);
      isFetchingCourses.current = false;
    }
  }, []);

  // Load courses for dropdown
  const loadCourses = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      const { courses, hasMore } = await fetchCourses(search, pageNum);

      const newOptions = courses.map((c) => ({
        value: c.id || c._id,
        label: c.code ? `${c.name} (${c.code})` : c.name,
        course: c,
      }));

      if (append) {
        setCourseOptions((prev) => [...prev, ...newOptions]);
      } else {
        setCourseOptions(newOptions);
      }

      setHasMoreCourses(hasMore);
      return { courses, hasMore };
    },
    [fetchCourses],
  );

  // Fetch trainers for dropdown
  const fetchTrainers = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingTrainers.current = true;
      setLoadingTrainers(true);
      const response = await getTrainers(20, pageNum, search);
      const trainers = response?.data || [];
      const hasMore = trainers.length === 20;
      return { trainers, hasMore };
    } catch (err) {
      console.error("Error fetching trainers:", err);
      return { trainers: [], hasMore: false };
    } finally {
      setLoadingTrainers(false);
      isFetchingTrainers.current = false;
    }
  }, []);

  // Load trainers for dropdown
  const loadTrainers = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      const { trainers, hasMore } = await fetchTrainers(search, pageNum);

      const newOptions = trainers.map((t) => ({
        value: t.id || t._id, // This is the actual trainer ObjectId from trainer collection
        label: t.trainerName
          ? `${t.trainerName} (${t.trainerEmail || ""})`
          : t.trainerEmail || "Unknown Trainer",
        trainer: t,
      }));

      if (append) {
        setTrainerOptions((prev) => [...prev, ...newOptions]);
      } else {
        setTrainerOptions(newOptions);
      }

      setHasMoreTrainers(hasMore);
      return { trainers, hasMore };
    },
    [fetchTrainers],
  );

  // Load more handlers
  const handleLoadMoreCourses = useCallback(async () => {
    if (!hasMoreCourses || loadingCourses || isFetchingCourses.current) return;
    const nextPage = coursePage + 1;
    setCoursePage(nextPage);
    await loadCourses(courseSearchTerm, nextPage, true);
  }, [
    hasMoreCourses,
    loadingCourses,
    coursePage,
    courseSearchTerm,
    loadCourses,
  ]);

  const handleLoadMoreTrainers = useCallback(async () => {
    if (!hasMoreTrainers || loadingTrainers || isFetchingTrainers.current)
      return;
    const nextPage = trainerPage + 1;
    setTrainerPage(nextPage);
    await loadTrainers(trainerSearchTerm, nextPage, true);
  }, [
    hasMoreTrainers,
    loadingTrainers,
    trainerPage,
    trainerSearchTerm,
    loadTrainers,
  ]);

  // Debounced search handlers
  const handleCourseSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setCourseSearchTerm(inputValue);
        setCoursePage(1);
        await loadCourses(inputValue, 1, false);
      }, 700),
    [loadCourses],
  );

  const handleTrainerSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setTrainerSearchTerm(inputValue);
        setTrainerPage(1);
        await loadTrainers(inputValue, 1, false);
      }, 700),
    [loadTrainers],
  );

  // Dropdown open handlers
  const handleCourseDropdownOpen = useCallback(async () => {
    if (!courseHasLoadedInitial && courseOptions.length === 0 && !loadingCourses) {
      await loadCourses("", 1, false);
      setCourseHasLoadedInitial(true);
    }
  }, [courseHasLoadedInitial, courseOptions.length, loadingCourses, loadCourses]);

  const handleTrainerDropdownOpen = useCallback(async () => {
    if (!trainerHasLoadedInitial && trainerOptions.length === 0 && !loadingTrainers) {
      await loadTrainers("", 1, false);
      setTrainerHasLoadedInitial(true);
    }
  }, [trainerHasLoadedInitial, trainerOptions.length, loadingTrainers, loadTrainers]);

  // Classrooms for the filter dropdown. 20 a page, well under the size at
  // which the classroom list endpoint switches to streaming.
  const CLASSROOM_PAGE_SIZE = 20;
  const loadClassrooms = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      isFetchingClassrooms.current = true;
      setLoadingClassrooms(true);
      try {
        const response = await getClassrooms(CLASSROOM_PAGE_SIZE, pageNum, search, search ? "classroomNo" : "");
        const classrooms = Array.isArray(response?.data) ? response.data : [];
        const newOptions = classrooms.map((c) => ({
          value: c.id || c._id,
          label: [c.classroomNo || "Unnamed classroom", c.floor].filter(Boolean).join(" · "),
          classroom: c,
        }));
        setClassroomOptions((prev) => (append ? [...prev, ...newOptions] : newOptions));
        setHasMoreClassrooms(classrooms.length === CLASSROOM_PAGE_SIZE);
      } catch (err) {
        console.error("Error fetching classrooms:", err);
        if (!append) setClassroomOptions([]);
        setHasMoreClassrooms(false);
      } finally {
        setLoadingClassrooms(false);
        isFetchingClassrooms.current = false;
      }
    },
    [],
  );

  const handleLoadMoreClassrooms = useCallback(async () => {
    if (!hasMoreClassrooms || loadingClassrooms || isFetchingClassrooms.current) return;
    const nextPage = classroomPage + 1;
    setClassroomPage(nextPage);
    await loadClassrooms(classroomSearchTerm, nextPage, true);
  }, [hasMoreClassrooms, loadingClassrooms, classroomPage, classroomSearchTerm, loadClassrooms]);

  const handleClassroomSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setClassroomSearchTerm(inputValue);
        setClassroomPage(1);
        await loadClassrooms(inputValue, 1, false);
      }, 700),
    [loadClassrooms],
  );

  const handleClassroomDropdownOpen = useCallback(async () => {
    if (!classroomHasLoadedInitial && classroomOptions.length === 0 && !loadingClassrooms) {
      await loadClassrooms("", 1, false);
      setClassroomHasLoadedInitial(true);
    }
  }, [classroomHasLoadedInitial, classroomOptions.length, loadingClassrooms, loadClassrooms]);

  // Fetch batches data with server-side filtering
  const fetchData = useCallback(async () => {
    if (!trainerDataLoaded) return;
    // Read before anything awaits: a ref reset mid-flight would turn an
    // append into a replace and snap the list back to its first page.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      // Determine which trainer ID to use for filtering
      let trainerIdParam = undefined;
      
      if (isTrainer && actualTrainerId) {
        // If logged in as trainer, use their actual trainer ID
        trainerIdParam = actualTrainerId;
      } else if (isAdmin && appliedTrainerFilter) {
        // If admin selected a trainer from filter, use the actual trainer ID (the value is already the ObjectId)
        trainerIdParam = appliedTrainerFilter;
      }

      console.log('Fetching batches with params:', {
        limit: recordsPerPage,
        page: currentPage,
        search: searchTerm,
        searchField: searchField,
        trainerId: trainerIdParam,
        courseId: appliedCourseFilter || undefined,
        classroomId: appliedClassroomFilter || undefined,
      });

      const response = await getBatches(
        recordsPerPage,
        currentPage,
        searchTerm,
        searchField,
        trainerIdParam,
        appliedCourseFilter || undefined,
        undefined,
        appliedClassroomFilter || undefined,
      );

      const pageData = response?.data || [];
      const pagination = response?.pagination || {};
      setTotalRecords(Number(pagination.total || 0));

      const pageOffset = (Number(currentPage) - 1) * recordsPerPage;

      const formattedData = pageData.map((batch, index) => {
        const studentInfo = getStudentCount(batch.userId);
        return {
          ...batch,
          srno: pageOffset + index + 1,
          courseName: getCourseName(batch.courseId),
          trainerName: getTrainerName(batch.trainerId),
          classroomName: getClassroomName(batch.classroom),
          branchName: getBranchName(batch.branch),
          displayStudentText: studentInfo.text,
          studentCount: studentInfo.count,
          startTimeFormatted: batch.startTime
            ? moment(batch.startTime).format("MMM Do YYYY, h:mm A")
            : "N/A",
          endTimeFormatted: batch.endTime
            ? moment(batch.endTime).format("MMM Do YYYY, h:mm A")
            : "N/A",
        };
      });

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...formattedData.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(formattedData);
      }
      appendRef.current = false;
    } catch (err) {
      console.error("Error fetching batches:", err);
      toast.error("Failed to fetch batch list");
      setData([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [
    currentPage,
    recordsPerPage,
    searchTerm,
    appliedTrainerFilter,
    appliedCourseFilter,
    appliedClassroomFilter,
    isTrainer,
    actualTrainerId,
    isAdmin,
    trainerDataLoaded,
  ]);

  // Load user profile on mount (only once)
  useEffect(() => {
    if (!hasLoaded.current) {
      fetchUserProfile();
      hasLoaded.current = true;
    }
  }, [fetchUserProfile]);

  // Fetch data whenever dependencies change (only after trainerDataLoaded is true)
  useEffect(() => {
    if (trainerDataLoaded) {
      fetchData();
    }
  }, [
    currentPage,
    recordsPerPage,
    searchTerm,
    appliedTrainerFilter,
    appliedCourseFilter,
    appliedClassroomFilter,
    fetchData,
    trainerDataLoaded,
  ]);

  // Apply filters
  const handleApplyFilters = () => {
    appendRef.current = false;
    setAppliedCourseFilter(selectedCourseFilter?.value || "");
    // When admin selects a trainer, we store the actual trainer ObjectId (value)
    setAppliedTrainerFilter(selectedTrainerFilter?.value || "");
    setAppliedClassroomFilter(selectedClassroomFilter?.value || "");
    setCurrentPage(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    appendRef.current = false;
    setSelectedCourseFilter(null);
    setSelectedTrainerFilter(null);
    setAppliedCourseFilter("");
    setAppliedTrainerFilter("");
    setCurrentPage(1);
    setCourseOptions([]);
    setTrainerOptions([]);
    setCourseHasLoadedInitial(false);
    setTrainerHasLoadedInitial(false);
    setCourseSearchTerm("");
    setTrainerSearchTerm("");
    setCoursePage(1);
    setTrainerPage(1);
    setSelectedClassroomFilter(null);
    setAppliedClassroomFilter("");
    setClassroomOptions([]);
    setClassroomHasLoadedInitial(false);
    setClassroomSearchTerm("");
    setClassroomPage(1);
  };

  // Handle search
  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSearchField(field);
    runSearch(value);
  };

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) { appendRef.current = false; setCurrentPage(page); }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  // Show students popup using data from the batch (no extra API call)
  // Show batch details
  const showBatchDetails = (batch) => {
    setSelectedBatch(batch);
    setStudentSearchTerm("");
    // Always opens on Details; the roster is a tab away, not a second
    // dialog you have to close this one to reach.
    setBatchTab("details");
    setShowBatchDetailsModal(true);
  };

  const closeBatchDialog = () => {
    setShowBatchDetailsModal(false);
    setSelectedBatch(null);
    setStudentSearchTerm("");
  };

  // Get enrolled students with search (using batch.userId directly)
  const getEnrolledStudents = () => {
    if (!selectedBatch) return [];
    const students = getStudentsFromBatch(selectedBatch);
    const sorted = [...students].sort((a, b) =>
      (a.studentName || "").localeCompare(b.studentName || ""),
    );
    if (!studentSearchTerm.trim()) return sorted;
    const lower = studentSearchTerm.toLowerCase();
    return sorted.filter(
      (s) =>
        (s.studentName || "").toLowerCase().includes(lower) ||
        (s.studentEmail || "").toLowerCase().includes(lower) ||
        (s.phone || "").toLowerCase().includes(lower),
    );
  };

  // Delete batch
  const performDelete = async (id, hardDelete) => {
    try {
      await deleteBatch(id, hardDelete);
      // Confirmed in the popup, which then closes itself.
      toast.success(
        hardDelete ? "Batch Deleted Permanently!" : "Batch Archived!",
      );
      fetchData();
    } catch {
      toast.error("Failed to delete the batch.");
    }
  };

  const handleDelete = async (id) => {
    const canHardDelete = currentUser?.isSuperAdmin;
    if (canHardDelete) {
      const result = await confirm({
        title: "Delete Batch",
        text: "Choose delete action:",
        tone: 'danger',
        showDenyButton: true,
        confirmButtonText: "Archive (Soft Delete)",
        denyButtonText: "Permanent Delete",
      });
      if (result.isConfirmed) await performDelete(id, false);
      else if (result.isDenied) {
        const confirm2 = await confirm({
          title: "Are you absolutely sure?",
          text: "This cannot be undone!",
          tone: 'danger',
          confirmButtonText: "Yes, Delete Permanently!",
        });
        if (confirm2.isConfirmed) await performDelete(id, true);
      }
    } else {
      const result = await confirm({
        title: "Archive Batch",
        text: "Are you sure?",
        confirmButtonText: "Yes, Archive it!",
      });
      if (result.isConfirmed) await performDelete(id, false);
    }
  };

  // Create chat group
  const handleCreateChatGroup = async (row) => {
    try {
      confirm({
        title: 'Create Chat Group',
        text: `Are you sure you want to create a chat group for batch "${row.name}"?`,
        confirmButtonText: 'Yes, Create it!',
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoading(true);
          try {
            const res = await createChatGroup(row.id);
            await fetchData();
            toast.success(res?.message || 'Chat Group Created Successfully!');
          } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to create chat group.');
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteChatGroup = async (row) => {
    const isSuperAdmin = currentUser?.isSuperAdmin;
    const canHardDelete = isSuperAdmin;

    if (canHardDelete) {
      confirm({
        title: 'Delete Chat Group',
        text: 'Choose delete action:',
        tone: 'danger',
        showDenyButton: true,
        confirmButtonText: 'Archive (Soft Delete)',
        denyButtonText: 'Permanent Delete',
        cancelButtonText: 'Cancel',
      }).then(async (result) => {
        if (result.isConfirmed) {
          // Soft Delete
          try {
            await deleteChatGroup(row.id, false);
            fetchData();
            toast.success('Chat Group Archived Successfully!');
          } catch (error) {
            toast.error('Failed to archive chat group.');
          }
        } else if (result.isDenied) {
          // Permanent Delete Confirmation
          confirm({
            title: 'Are you absolutely sure?',
            text: 'This will permanently delete the chat group and cannot be undone!',
            tone: 'danger',
            confirmButtonText: 'Yes, Delete Permanently!',
            cancelButtonText: 'No, Cancel',
          }).then(async (res) => {
            if (res.isConfirmed) {
              try {
                await deleteChatGroup(row.id, true);
                fetchData();
                toast.success('Chat Group Deleted Permanently!');
              } catch (error) {
                toast.error('Failed to delete chat group.');
              }
            }
          });
        }
      });
    } else {
      // Non-superadmin: Archive only
      confirm({
        title: 'Archive Chat Group',
        text: 'Are you sure you want to archive this chat group?',
        confirmButtonText: 'Yes, Archive it!',
        cancelButtonText: 'No, Cancel',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteChatGroup(row.id, false);
            fetchData();
            toast.success('Chat Group Archived Successfully!');
          } catch (error) {
            toast.error('Failed to archive chat group.');
          }
        }
      });
    }
  };

  const handleSyncChatMembers = async (row) => {
    try {
      const result = await confirm({
        title: 'Sync Chat Members',
        text: `Retry adding pending students to the chat group for "${row.name}"?`,
        confirmButtonText: 'Yes, Sync!',
      });
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const res = await syncChatMembers(row.id);
          await fetchData();
          toast.success(res?.message || 'Chat members synced successfully!');
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Failed to sync chat members.');
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Table headers - Students column now as a button
  const headers = [
    {
      title: "Batch",
      prop: "name",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.name}</span>
          {row.courseName && row.courseName !== "N/A" && (
            <span className="lp-sub">{row.courseName}</span>
          )}
        </div>
      ),
    },
    {
      title: "Trainer",
      prop: "trainerName",
      sortable: true,
      cell: (row) => (
        <span className="lp-plain">{row.trainerName || "—"}</span>
      ),
    },
    {
      title: "Branch",
      prop: "branchName",
      cell: (row) => (
        <span className="lp-plain">
          {row.branchName && row.branchName !== "N/A" ? row.branchName : "—"}
        </span>
      ),
    },
    {
      title: "Runs",
      prop: "startTimeFormatted",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-when">{row.startTimeFormatted}</span>
          <span className="lp-sub">to {row.endTimeFormatted}</span>
        </div>
      ),
    },
    {
      title: "Students",
      prop: "studentCount",
      sortable: true,
      cell: (row) => <span className="lp-num">{row.studentCount ?? 0}</span>,
    },
    {
      title: "Mode",
      prop: "mode",
      cell: (row) => (
        <span className="pg-pill">{row.mode || "—"}</span>
      ),
    },
    /* Add or remove students in place. Only for those who may edit a batch
       -- it is the same PUT the edit form makes. hasPermission, not canEdit:
       canEdit is declared below this array. */
    ...(hasPermission("batches", "update", "lms")
      ? [
          {
            title: "",
            prop: "manageStudents",
            cell: (row) => (
              <div className="pgm-actions" style={{ flexWrap: "nowrap" }}>
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={(e) => {
                    // The row opens the batch view on click; this opens its own.
                    e.stopPropagation();
                    setManageBatch({ id: row.id, name: row.name });
                  }}
                  aria-haspopup="dialog"
                  title="Add or remove students"
                >
                  <UserPlus aria-hidden="true" />
                  Students
                </button>
                {/* Same action as the popup footer's Create chat group. */}
                {!row.chatGroupId && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateChatGroup(row);
                    }}
                    title="Create chat group"
                    aria-label="Create chat group"
                  >
                    <MessageSquarePlus aria-hidden="true" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const enrolledStudents = getEnrolledStudents();

  const canEdit = hasPermission("batches", "update", "lms");
  const canDelete = hasPermission("batches", "delete", "lms");

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission("batches", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              /* The same dialog Edit opens, with no record behind it.
                 This navigated to /add-batch, which left the list --
                 and its filters, its scroll position and its loaded
                 pages -- to come back to from scratch afterwards. */
              onClick={() => setFormModal({ recordId: null })}
            >
              Add batch
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter batches">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-b-search">
              Search
            </label>
            <input
              id="lp-b-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by batch name, course or trainer..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-b-field">
              Search in
            </label>
            <select
              id="lp-b-field"
              className="pg-select"
              value={searchField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              <option value="name">Batch name</option>
              <option value="mode">Mode</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-b-rows">
              Rows per load
            </label>
            <select
              id="lp-b-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {isAdmin && (
          <div className="pg-filters-row">
            <div className="pg-filters-fields pg-filters-fields--three">
              <div className="pg-field">
                <label className="pg-label">Course</label>
                <NewCustomAsyncDropdown
                  options={courseOptions}
                  value={selectedCourseFilter ? [selectedCourseFilter] : []}
                  onChange={(opts) =>
                    setSelectedCourseFilter(
                      Array.isArray(opts) && opts.length
                        ? opts[opts.length - 1]
                        : null,
                    )
                  }
                  placeholder="All courses"
                  isMulti={false}
                  isSearchable={true}
                  searchInControl={true}
                  isLoading={loadingCourses}
                  onInputChange={handleCourseSearch}
                  onMenuScrollToBottom={handleLoadMoreCourses}
                  onMenuOpen={handleCourseDropdownOpen}
                  loadingMessage="Loading courses..."
                  noOptionsMessage="No courses available"
                  noSearchResultsMessage="No courses found"
                />
              </div>

              <div className="pg-field">
                <label className="pg-label">Trainer</label>
                <NewCustomAsyncDropdown
                  options={trainerOptions}
                  value={selectedTrainerFilter ? [selectedTrainerFilter] : []}
                  onChange={(opts) =>
                    setSelectedTrainerFilter(
                      Array.isArray(opts) && opts.length
                        ? opts[opts.length - 1]
                        : null,
                    )
                  }
                  placeholder="All trainers"
                  isMulti={false}
                  isSearchable={true}
                  searchInControl={true}
                  isLoading={loadingTrainers}
                  onInputChange={handleTrainerSearch}
                  onMenuScrollToBottom={handleLoadMoreTrainers}
                  onMenuOpen={handleTrainerDropdownOpen}
                  loadingMessage="Loading trainers..."
                  noOptionsMessage="No trainers available"
                  noSearchResultsMessage="No trainers found"
                />
              </div>

              <div className="pg-field">
                <label className="pg-label">Classroom</label>
                <NewCustomAsyncDropdown
                  options={classroomOptions}
                  value={selectedClassroomFilter ? [selectedClassroomFilter] : []}
                  onChange={(opts) =>
                    setSelectedClassroomFilter(
                      Array.isArray(opts) && opts.length
                        ? opts[opts.length - 1]
                        : null,
                    )
                  }
                  placeholder="All classrooms"
                  isMulti={false}
                  isSearchable={true}
                  searchInControl={true}
                  isLoading={loadingClassrooms}
                  onInputChange={handleClassroomSearch}
                  onMenuScrollToBottom={handleLoadMoreClassrooms}
                  onMenuOpen={handleClassroomDropdownOpen}
                  loadingMessage="Loading classrooms..."
                  noOptionsMessage="No classrooms available"
                  noSearchResultsMessage="No classrooms found"
                />
              </div>
            </div>

            <div className="pg-filters-actions">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
              <button
                type="button"
                className="pg-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => showBatchDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more batches...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} batches
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} batch{totalRecords === 1 ? "" : "es"} loaded
            </span>
          )
        )}
      </div>

      {/* One batch, in two tabs. The roster used to be a second dialog
          opened from the first, which meant closing one to read the other
          and losing the batch you were looking at. */}
      {showBatchDetailsModal && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-batch-title"
          onMouseDown={(e) => {
            if (e.target !== e.currentTarget) return;
            closeBatchDialog();
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--xl">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Batch</span>
                <h3 className="pg-modal-title" id="lp-batch-title">
                  {selectedBatch?.name}
                </h3>
                {selectedBatch?.courseName && (
                  <p className="pg-modal-sub">{selectedBatch.courseName}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={closeBatchDialog}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            {!batchDone && (
            <div className="lp-tabs" role="tablist" aria-label="Batch">
              <button
                type="button"
                role="tab"
                id="lp-tab-details"
                aria-selected={batchTab === "details"}
                aria-controls="lp-panel-details"
                className={`lp-tab${batchTab === "details" ? " is-on" : ""}`}
                onClick={() => setBatchTab("details")}
              >
                Details
              </button>
              <button
                type="button"
                role="tab"
                id="lp-tab-students"
                aria-selected={batchTab === "students"}
                aria-controls="lp-panel-students"
                className={`lp-tab${batchTab === "students" ? " is-on" : ""}`}
                onClick={() => setBatchTab("students")}
              >
                Students
                <span className="lp-tab-n">
                  {selectedBatch?.studentCount || 0}
                </span>
              </button>
              <button
                type="button"
                role="tab"
                id="lp-tab-notes"
                aria-selected={batchTab === "notes"}
                aria-controls="lp-panel-notes"
                className={`lp-tab${batchTab === "notes" ? " is-on" : ""}`}
                onClick={() => setBatchTab("notes")}
              >
                Batch Notes
                <span className="lp-tab-n">
                  {notesState === "ready" ? batchNotes.length : selectedBatch?.noteCount || 0}
                </span>
              </button>
            </div>
            )}

            <div className="pg-modal-body">
              {batchDone ? (
                <PopupDone message={batchDone} onClose={closeBatchDialog} />
              ) : batchTab === "details" ? (
                <div
                  role="tabpanel"
                  id="lp-panel-details"
                  aria-labelledby="lp-tab-details"
                >
                  {selectedBatch && (
                    <dl className="quiz-facts">
                      <div>
                        <dt>Mode</dt>
                        <dd>{selectedBatch.mode || "—"}</dd>
                      </div>
                      <div>
                        <dt>Trainer</dt>
                        <dd>{selectedBatch.trainerName || "—"}</dd>
                      </div>
                      <div>
                        <dt>Classroom</dt>
                        <dd>{selectedBatch.classroomName || "—"}</dd>
                      </div>
                      <div>
                        <dt>Branch</dt>
                        <dd>
                          {selectedBatch.branchName && selectedBatch.branchName !== "N/A"
                            ? selectedBatch.branchName
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>Students</dt>
                        <dd>{selectedBatch.studentCount || 0}</dd>
                      </div>
                      <div>
                        <dt>Starts</dt>
                        <dd>{formatDate(selectedBatch.startTime)}</dd>
                      </div>
                      <div>
                        <dt>Ends</dt>
                        <dd>{formatDate(selectedBatch.endTime)}</dd>
                      </div>
                    </dl>
                  )}

                  {/* The latest notes, so the Details tab says what has been
                      happening with the batch without a second click. */}
                  <section className="lp-recent-notes" aria-label="Recent notes">
                    <header className="lp-recent-notes-head">
                      <h4 className="lp-recent-notes-title">Recent notes</h4>
                      {recentNotes.total > 0 && (
                        <button
                          type="button"
                          className="wp-link"
                          onClick={() => setBatchTab("notes")}
                        >
                          {recentNotes.total > recentNotes.items.length
                            ? `See all ${recentNotes.total} notes`
                            : "Open Batch Notes"}
                        </button>
                      )}
                    </header>
                    {recentNotes.state === "loading" && recentNotes.items.length === 0 ? (
                      <p className="lp-muted">Loading notes...</p>
                    ) : recentNotes.state === "failed" ? (
                      <p className="lp-muted">The notes did not load.</p>
                    ) : recentNotes.items.length === 0 ? (
                      <p className="lp-muted">No notes on this batch yet. Add one from Edit batch.</p>
                    ) : (
                      <ul className="lp-notes">
                        {recentNotes.items.map((n, i) => (
                          <li className="lp-note" key={`${n.date}-${i}`}>
                            <p className="lp-note-text">{n.note}</p>
                            <p className="lp-note-meta">
                              <b>{n.userName}</b>
                              {n.date && ` · ${moment(n.date).format("D MMM YYYY, h:mm A")}`}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
              ) : batchTab === "notes" ? (
                <div
                  role="tabpanel"
                  id="lp-panel-notes"
                  aria-labelledby="lp-tab-notes"
                >
                  {notesState === "loading" ? (
                    <p className="lp-muted">Loading notes...</p>
                  ) : notesState === "failed" ? (
                    <p className="lp-muted">The notes did not load. Try again in a moment.</p>
                  ) : batchNotes.length === 0 ? (
                    <p className="lp-muted">
                      No notes on this batch yet. Add one from Edit batch.
                    </p>
                  ) : (
                    <ul className="lp-notes">
                      {batchNotes.map((n, i) => (
                        <li className="lp-note" key={`${n.date}-${i}`}>
                          <p className="lp-note-text">{n.note}</p>
                          <p className="lp-note-meta">
                            <b>{n.userName}</b>
                            {n.date && ` · ${moment(n.date).format("D MMM YYYY, h:mm A")}`}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div
                  role="tabpanel"
                  id="lp-panel-students"
                  aria-labelledby="lp-tab-students"
                >
                  <div className="pg-field pg-field--search lp-dialog-search">
                    <label className="pg-label" htmlFor="lp-batch-stu-search">
                      Search
                    </label>
                    <input
                      id="lp-batch-stu-search"
                      type="search"
                      className="pg-input pg-input--search"
                      placeholder="Search by name, email or phone..."
                      value={studentSearchTerm}
                      onChange={(e) => setStudentSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="lp-dialog-table">
                    {enrolledStudents.length === 0 ? (
                      <p className="lp-muted">
                        {studentSearchTerm
                          ? "No student on this batch matches what you typed."
                          : "No students are enrolled in this batch."}
                      </p>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enrolledStudents.map((student, index) => (
                            <tr key={student.studentId}>
                              <td>{index + 1}</td>
                              <td>{student.studentName}</td>
                              <td>{student.studentEmail}</td>
                              <td>{student.phone}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>

            {!batchDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && selectedBatch && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = selectedBatch.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit batch
                  </button>
                )}
                {/* Chat group: create one when the batch has none; once it
                    exists, sync students still pending an auth profile and
                    archive / delete the group. */}
                {canEdit && selectedBatch && !selectedBatch.chatGroupId && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const row = selectedBatch;
                      handleCreateChatGroup(row);
                    }}
                  >
                    Create chat group
                  </button>
                )}
                {canEdit && selectedBatch?.chatGroupId && selectedBatch.pendingChatMembers && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const row = selectedBatch;
                      handleSyncChatMembers(row);
                    }}
                  >
                    Sync chat members
                  </button>
                )}
                {(canEdit || canDelete) && selectedBatch?.chatGroupId && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const row = selectedBatch;
                      handleDeleteChatGroup(row);
                    }}
                  >
                    Delete chat group
                  </button>
                )}
                {canDelete && selectedBatch && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = selectedBatch.id;
                      handleDelete(id);
                    }}
                  >
                    Delete batch
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={closeBatchDialog}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}

      {/* ── Add / edit dialog ─────────────────────────────────────────────
          One dialog for both: { recordId: null } adds, { recordId } edits.
          AddBatch reads `batchId ?? routeId`, so with no record and no
          route param it opens in add mode on its own.

          The same shell the events list uses for its own form. The panel is
          --xl because the batch form is five sections wide, and --form is
          what lets a form's own footer sit on the panel's bottom edge. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div
            className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lp-batch-form-title"
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Batch</span>
                <h3 className="pg-modal-title" id="lp-batch-form-title">
                  {formModal.recordId ? "Edit batch" : "Add a batch"}
                </h3>
                {formModal.name && (
                  <p className="pg-modal-sub">{formModal.name}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <AddBatch
                batchId={formModal.recordId}
                onCancel={() => setFormModal(null)}
                onSaved={() => {
                  // Saved: the form's toast becomes this dialog's done panel,
                  // which closes the dialog by itself.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  closeBatchDialog();
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchData();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Students dialog ───────────────────────────────────────────────
          Search and add students, and remove existing ones, in one view --
          without the five-section edit form. BatchStudentsManager is the
          same roster editor the batch report's Manage step uses: nothing is
          sent until its Save, and removals are confirmed. The list reloads
          after a save so the Students count is current; the dialog shows
          the done panel and closes itself. */}
      {manageBatch && (
        <div
          className="pg-modal"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setManageBatch(null);
          }}
        >
          <div
            className="pg-modal-panel pg-modal-panel--xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lp-batch-students-title"
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Students</span>
                <h3 className="pg-modal-title" id="lp-batch-students-title">
                  {manageBatch.name || "Batch"}
                </h3>
                <p className="pg-modal-sub">Search to add students, or remove the ones already in it.</p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setManageBatch(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {manageDone ? (
                <PopupDone message={manageDone} onClose={() => setManageBatch(null)} />
              ) : (
              <BatchStudentsManager
                batchId={manageBatch.id}
                onSaved={() => {
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchData();
                }}
              />
              )}
            </div>

            {!manageDone && (
            <footer className="pg-modal-foot">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setManageBatch(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListBatch;
